import React from "react";
//#region types
import { BaseFormFieldProps, WintFormKey } from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { InvoiceFormTypes } from "models/formTypes";
import { SelfInvoiceItemRowProps } from "models/propTypes/selfInvoicePropTypes";
//#endregion
//#region components
import { TableRow, TableCell } from "@mui/material";
import { CurrencyInput, NumberInput, SelectInput } from "components/common/wintForms";
import { setIn } from "formik";
import { Icon, RoundButton } from "components/common";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

function SelfInvoiceItemRow(props: SelfInvoiceItemRowProps) {
  const { connect, index, row, updateRow, removeRow, isLastRow, units, onUnitSelect } = props;

  const rowName = `products[${index}]`;

  const setRowField = (name: WintFormKey<InvoiceFormTypes.Product>, value: any, shouldValidate?: boolean) => {
    const newRow = setIn(row, name, value);
    updateRow(newRow, shouldValidate);
  };
  const handleChange = (name: WintFormKey<InvoiceFormTypes.Product>) => (e: React.ChangeEvent<any>) => {
    setRowField(name, e.target.value);
  };

  const connectRowToForm = (name: WintFormKey<InvoiceFormTypes.Product>): BaseFormFieldProps => {
    const connector = connect((rowName + "." + name) as WintFormKey<InvoiceFormTypes.SelfInvoice>);

    return {
      name: connector.name,
      value: connector.value,
      onChange: handleChange(name),
      onBlur: (e: React.FocusEvent<any>) => {
        connector.onBlur(e);
        connector.validate();
      },
      setValue: (value: any, shouldValidate?: boolean) => setRowField(name, value, shouldValidate),
      setTouched: (isTouched?: boolean, shouldValidate?: boolean) => connector.setTouched(isTouched, shouldValidate),
      touched: connector.touched,
      error: connector.error,
      hasError: connector.hasError,
    };
  };

  const removeRowEvent = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    removeRow(index);
  };

  const onRowClick = React.useCallback(() => {
    if (isLastRow) {
      updateRow({ ...row }, index === 0);
    }
  }, [isLastRow]);

  const unitProps = connectRowToForm("unit");
  const quantityProps = connectRowToForm("quantity");

  return (
    <TableRow className={clsx(cssExports.fieldRow, isLastRow ? cssExports.tableLastRow : "")} onClick={onRowClick}>
      <TableCell>{isLastRow ? "" : index + 1}</TableCell>
      <TableCell style={{ width: "25%" }}>
        <NumberInput
          {...quantityProps}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Quantity)}
          allowLeadingZeros={false}
          decimalPoints={2}
          disabled={!row.unit}
        />
      </TableCell>
      <TableCell style={{ width: "25%" }}>
        <SelectInput
          {...unitProps}
          setValue={onUnitSelect}
          value={unitProps.value ?? 0}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Unit)}
          items={units}
        />
      </TableCell>
      <TableCell>
        <CurrencyInput
          {...connectRowToForm("totalAmountExTax")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.TotalPriceExVAT)}
        />
      </TableCell>
      <TableCell className={cssExports.editableTableActionButton}>
        {!isLastRow ? (
          <RoundButton
            size="xs"
            displayType={StyleEnums.DisplayType.None}
            className={cssExports.close}
            onClick={removeRowEvent}
          >
            <Icon icon={IconDefinitionConstants.WTimes} />
          </RoundButton>
        ) : (
          <></>
        )}
      </TableCell>
    </TableRow>
  );
}

export default SelfInvoiceItemRow;
